<template>
    <div
        class="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
    >
        <div class="border-b border-gray-100 px-5 py-3 dark:border-gray-700/50">
            <h3 class="text-sm font-medium text-gray-900 dark:text-white">
                Consulta e exceção por oferta
            </h3>
            <p class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                Busque pelo código público da oferta para ver a liberação, o link do checkout e de
                onde a liberação veio.
            </p>
        </div>

        <form class="flex items-end gap-2 px-5 py-3" @submit.prevent="search">
            <div class="grow">
                <text-input v-model="code" label="Código da oferta" placeholder="OEXPCRS1" />
            </div>
            <button
                type="submit"
                class="rounded-md bg-gray-900 px-4 py-2 text-xs font-medium text-white hover:bg-gray-800 disabled:opacity-50 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200"
                :disabled="loading"
            >
                {{ loading ? "Buscando..." : "Buscar" }}
            </button>
        </form>

        <div
            v-if="searchError"
            class="mx-5 mb-4 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300"
        >
            {{ searchError }}
        </div>

        <template v-if="result">
            <div class="space-y-2 px-5 pb-1">
                <p
                    v-if="result.offer.deleted"
                    class="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200"
                >
                    Esta oferta está excluída. Ela não recebe exceção nova; só é possível voltar a
                    "segue o produtor".
                </p>
                <p
                    v-if="!result.offer.is_active"
                    class="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200"
                >
                    Esta oferta está inativa. A exceção é aceita mesmo assim.
                </p>
                <p
                    v-if="result.offer.is_hidden"
                    class="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200"
                >
                    Esta oferta está oculta: não é vendida direto pelo link do checkout, só no
                    fluxo interno de upsell. A exceção é aceita mesmo assim.
                </p>
                <p
                    v-if="result.offer.producer.deleted"
                    class="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200"
                >
                    O produtor desta oferta está excluído.
                </p>
                <p
                    v-if="result.propagating"
                    class="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200"
                >
                    Mudança em propagação: o comprador pode ver o valor anterior por até 60
                    segundos.
                </p>
                <p
                    v-if="result.explanation_error"
                    class="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300"
                >
                    Consulta indisponível: não foi possível consultar a liberação no Payments
                    agora. A marcação gravada continua valendo.
                </p>
            </div>

            <dl class="divide-y divide-gray-100 dark:divide-gray-700/50">
                <div class="grid grid-cols-3 gap-4 px-5 py-3">
                    <dt class="text-xs text-gray-500 dark:text-gray-400">Oferta</dt>
                    <dd class="col-span-2 text-sm text-gray-900 dark:text-white">
                        <span class="font-mono">{{ result.offer.code }}</span>
                        — {{ result.offer.name }}
                    </dd>
                </div>
                <div class="grid grid-cols-3 gap-4 px-5 py-3">
                    <dt class="text-xs text-gray-500 dark:text-gray-400">Produto</dt>
                    <dd class="col-span-2 text-sm text-gray-900 dark:text-white">
                        {{ result.offer.product.name || "—" }}
                    </dd>
                </div>
                <div class="grid grid-cols-3 gap-4 px-5 py-3">
                    <dt class="text-xs text-gray-500 dark:text-gray-400">Produtor</dt>
                    <dd class="col-span-2 text-sm text-gray-900 dark:text-white">
                        {{ result.offer.producer.name || "—" }}
                    </dd>
                </div>
                <div class="grid grid-cols-3 gap-4 px-5 py-3">
                    <dt class="text-xs text-gray-500 dark:text-gray-400">Nível do produtor</dt>
                    <dd class="col-span-2 text-sm text-gray-900 dark:text-white">
                        {{ producerValueLabel }}
                    </dd>
                </div>
                <div class="grid grid-cols-3 gap-4 px-5 py-3">
                    <dt class="text-xs text-gray-500 dark:text-gray-400">Chave global</dt>
                    <dd class="col-span-2 text-sm text-gray-900 dark:text-white">
                        {{ switchLabel }}
                    </dd>
                </div>
                <div class="grid grid-cols-3 gap-4 px-5 py-3">
                    <dt class="text-xs text-gray-500 dark:text-gray-400">Exceção atual</dt>
                    <dd class="col-span-2 text-sm text-gray-900 dark:text-white">
                        {{ offerValueLabel }}
                    </dd>
                </div>
                <div class="grid grid-cols-3 gap-4 px-5 py-3">
                    <dt class="text-xs text-gray-500 dark:text-gray-400">Liberação</dt>
                    <dd class="col-span-2 text-sm font-medium text-gray-900 dark:text-white">
                        {{ experienceLabel }}
                    </dd>
                </div>
                <div class="grid grid-cols-3 gap-4 px-5 py-3">
                    <dt class="text-xs text-gray-500 dark:text-gray-400">Link do checkout</dt>
                    <dd class="col-span-2 text-sm text-gray-900 dark:text-white">
                        <template v-if="checkoutUrl">
                            <span class="break-all font-mono text-xs">{{ checkoutUrl }}</span>
                            <button
                                type="button"
                                class="ml-2 rounded-md border border-gray-300 px-2 py-0.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                                @click="copyCheckoutUrl"
                            >
                                Copiar
                            </button>
                        </template>
                        <span v-else>{{ unavailable }}</span>
                    </dd>
                </div>
                <div class="grid grid-cols-3 gap-4 px-5 py-3">
                    <dt class="text-xs text-gray-500 dark:text-gray-400">Origem</dt>
                    <dd class="col-span-2 text-sm text-gray-900 dark:text-white">
                        {{ originLabel }}
                    </dd>
                </div>
                <div v-if="funnel" class="grid grid-cols-3 gap-4 px-5 py-3">
                    <dt class="text-xs text-gray-500 dark:text-gray-400">Funil</dt>
                    <dd class="col-span-2 text-sm text-gray-900 dark:text-white">
                        <p>{{ funnelSummary }}</p>
                        <p v-if="holdingLabel" class="mt-1">{{ holdingLabel }}</p>

                        <ul v-if="visibleMembers.length" class="mt-2 space-y-1">
                            <li
                                v-for="member in visibleMembers"
                                :key="member.code"
                                class="text-xs text-gray-600 dark:text-gray-300"
                            >
                                <span class="font-mono">{{ member.code }}</span>
                                — {{ member.name || "—" }} ·
                                {{ experienceLabelFor(member.experience) }} ·
                                {{ originLabelFor(member.origin) }}
                            </li>
                        </ul>

                        <!-- US-014 EC-5: funil com centenas de ofertas não é listado de uma vez. -->
                        <button
                            v-if="hiddenMembersCount"
                            type="button"
                            class="mt-1 text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                            @click="showAllMembers = true"
                        >
                            Ver as outras {{ hiddenMembersCount }}
                        </button>

                        <p v-if="ignoredLabel" class="mt-2 text-xs text-gray-500 dark:text-gray-400">
                            {{ ignoredLabel }}
                        </p>
                    </dd>
                </div>
            </dl>

            <div class="border-t border-gray-100 px-5 py-3 dark:border-gray-700/50">
                <button
                    type="button"
                    class="rounded-md bg-gray-900 px-4 py-2 text-xs font-medium text-white hover:bg-gray-800 disabled:opacity-50 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200"
                    :disabled="loading"
                    @click="modalOpen = true"
                >
                    Mudar a exceção desta oferta
                </button>
            </div>
        </template>

        <experience-change-modal
            v-if="result"
            :open="modalOpen"
            title="Exceção desta oferta"
            :description="
                result.offer.code + ' — ' + result.offer.name
            "
            :options="options"
            :current-value="result.value === null ? 'follow' : result.value"
            :submitting="submitting"
            @close="modalOpen = false"
            @confirm="submit"
        >
            <template #scope="{ value }">
                <p>
                    A exceção vale só para esta oferta e vence o nível do produtor. A mudança chega
                    ao comprador em até 60 segundos.
                </p>
                <p v-if="value === 'v2'" class="mt-1">{{ customDomainNote }}</p>
            </template>
        </experience-change-modal>
    </div>
</template>

<script>
import TextInput from "../../forms/TextInput.vue";
import ExperienceChangeModal from "./ExperienceChangeModal.vue";

const SWITCH_LABELS = { off: "Desligado", selective: "Seletivo", all: "Todos" };

const EXPERIENCE_LABELS = { legacy: "Legado", v2: "Checkout 2.0" };

// Mesmos rótulos do ProducerExperienceSection.vue: a origem vem do mesmo enum do Payments.
const ORIGIN_LABELS = {
    kill_switch: "Chave global em Desligado",
    offer: "Exceção da oferta",
    producer: "Produtor",
    default: "Padrão da chave global",
    unresolvable: "Oferta não resolvível",
    custom_domain: "Domínio próprio",
    funnel: "Funil",
    funnel_limit: "Funil acima do limite",
    source_unavailable: "Fonte indisponível",
    v2_unconfigured: "Host do Checkout 2.0 não configurado",
};

const CUSTOM_DOMAIN_NOTE =
    "Uma oferta com domínio próprio é retida por domínio próprio e fica no Legado: a exceção Checkout 2.0 é recusada até o domínio estar no Bolt 2.0 (card 6.4).";

// Funil grande não é listado de uma vez (US-014 EC-5).
const MEMBERS_PREVIEW = 10;

const UNAVAILABLE = "Consulta indisponível";

export default {
    name: "OfferExperienceLookup",

    components: { ExperienceChangeModal, TextInput },

    emits: ["changed"],

    data() {
        return {
            code: "",
            loading: false,
            submitting: false,
            modalOpen: false,
            searchError: "",
            result: null,
            showAllMembers: false,
            customDomainNote: CUSTOM_DOMAIN_NOTE,
            unavailable: UNAVAILABLE,
        };
    },

    computed: {
        producerValueLabel() {
            if (!this.result) {
                return "";
            }

            return this.result.producer_value === null
                ? "segue a chave global"
                : EXPERIENCE_LABELS[this.result.producer_value];
        },

        offerValueLabel() {
            if (!this.result) {
                return "";
            }

            return this.result.value === null
                ? "segue o produtor"
                : EXPERIENCE_LABELS[this.result.value];
        },

        switchLabel() {
            const explanation = this.result?.explanation;

            if (!explanation) {
                return UNAVAILABLE;
            }

            // Oferta não resolvível: o Payments não lê a chave, que não entra na decisão.
            return explanation.switch ? SWITCH_LABELS[explanation.switch] : "—";
        },

        experienceLabel() {
            const value = this.result?.explanation?.experience;

            return value ? EXPERIENCE_LABELS[value] : UNAVAILABLE;
        },

        originLabel() {
            const value = this.result?.explanation?.origin;

            return value ? this.originLabelFor(value) : UNAVAILABLE;
        },

        checkoutUrl() {
            return this.result?.explanation?.checkout_url || "";
        },

        funnel() {
            return this.result?.explanation?.funnel || null;
        },

        funnelSummary() {
            if (!this.funnel) {
                return "";
            }

            const total =
                this.funnel.size === 1
                    ? "Só esta oferta no funil"
                    : this.funnel.size + " ofertas no funil, com esta";

            return total + " (limite de " + this.funnel.limit + ").";
        },

        holdingLabel() {
            if (!this.funnel) {
                return "";
            }

            if (this.funnel.limit_exceeded) {
                return "Retida pelo funil: ele passa do limite de " + this.funnel.limit + " ofertas.";
            }

            const holding = this.funnel.holding;

            if (!holding) {
                return "Nenhuma oferta do funil segura esta.";
            }

            return (
                "Retida pelo funil: " +
                holding.code +
                " — " +
                this.originLabelFor(holding.origin) +
                "."
            );
        },

        visibleMembers() {
            const members = this.funnel?.members || [];

            return this.showAllMembers ? members : members.slice(0, MEMBERS_PREVIEW);
        },

        hiddenMembersCount() {
            return (this.funnel?.members?.length || 0) - this.visibleMembers.length;
        },

        ignoredLabel() {
            const ignored = this.funnel?.ignored || [];

            return ignored.length
                ? "Referências ignoradas (oferta inexistente ou excluída): " + ignored.join(", ")
                : "";
        },

        options() {
            // Oferta excluída não recebe exceção nova, mas pode ter a dela removida (BR-17).
            // O valor atual continua selecionável: o admin pode desmarcar e voltar a ele.
            const deleted = this.result?.offer?.deleted === true;
            const blocked = (value) => deleted && this.result?.value !== value;

            return [
                {
                    value: "follow",
                    label: "segue o produtor",
                    help: "Remove a exceção. A oferta volta a usar o nível do produtor.",
                },
                {
                    value: "legacy",
                    label: "Legado",
                    help: "Força o legado nesta oferta, mesmo com o produtor em Checkout 2.0.",
                    disabled: blocked("legacy"),
                },
                {
                    value: "v2",
                    label: "Checkout 2.0",
                    help: "Libera esta oferta para o Checkout 2.0, mesmo com o produtor no Legado.",
                    disabled: blocked("v2"),
                },
            ];
        },
    },

    methods: {
        // Código digitado com espaços ou em minúsculas precisa achar a oferta (US-013 EC-1).
        normalize(code) {
            return code.trim().toUpperCase();
        },

        originLabelFor(origin) {
            return ORIGIN_LABELS[origin] || origin;
        },

        experienceLabelFor(experience) {
            return EXPERIENCE_LABELS[experience] || experience;
        },

        copyCheckoutUrl() {
            navigator.clipboard
                .writeText(this.checkoutUrl)
                .then(() => {
                    this.$notify(
                        {
                            group: "success",
                            title: "Link copiado",
                            text: "O link do checkout desta oferta está na área de transferência.",
                        },
                        3000,
                    );
                })
                .catch(() => {
                    this.$notify(
                        {
                            group: "error",
                            title: "Erro",
                            text: "Não foi possível copiar o link. Copie manualmente.",
                        },
                        4000,
                    );
                });
        },

        search() {
            const code = this.normalize(this.code);

            if (code === "") {
                this.searchError = "Informe o código da oferta.";
                this.result = null;
                return;
            }

            this.loading = true;
            this.searchError = "";
            this.showAllMembers = false;

            axios
                .get("/api/checkout-experience/offers/" + encodeURIComponent(code))
                .then(({ data }) => {
                    this.result = data;
                })
                .catch(({ response }) => {
                    this.result = null;

                    if (response?.status === 404) {
                        this.searchError =
                            "Oferta não encontrada. O endpoint responde Legado para esse código.";
                        return;
                    }

                    this.searchError = this.errorText(
                        response,
                        "Falha ao consultar a oferta.",
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        refresh() {
            if (this.result) {
                this.search();
            }
        },

        submit({ value, reason }) {
            if (this.submitting) {
                return;
            }

            this.submitting = true;

            axios
                .put(
                    "/api/checkout-experience/offers/" +
                        encodeURIComponent(this.result.offer.code),
                    { value, reason },
                )
                .then(({ data }) => {
                    this.modalOpen = false;

                    this.$notify(
                        {
                            group: data.changed ? "success" : "error",
                            title: data.changed ? "Exceção atualizada" : "Nada mudou",
                            text: data.changed
                                ? "A nova exceção vale para o comprador em até 60 segundos."
                                : "A oferta já estava nesse valor. Nada foi registrado.",
                        },
                        4000,
                    );

                    // O pai recarrega listas, histórico e esta consulta (refresh()).
                    this.$emit("changed");
                })
                .catch(({ response }) => {
                    this.$notify(
                        {
                            group: "error",
                            title: "Erro",
                            text: this.errorText(response, "Falha ao mudar a exceção."),
                        },
                        6000,
                    );
                })
                .finally(() => {
                    this.submitting = false;
                });
        },

        errorText(response, fallback) {
            const data = response?.data;

            if (Array.isArray(data?.errors) && data.errors.length) {
                return data.errors.join(" ");
            }

            return data?.message || fallback;
        },
    },
};
</script>
